import {
  PROMOTION_TYPE,
  TARGET_TYPE
} from '@/modules/dataMaintenance/constants/Special.constants'
import moment from 'moment-timezone'
import { formatRegionDisplayName } from '@/utils/stateDisplay'

export const DEFAULT_PROMOTION_TIER = {
  amountOff: undefined,
  numberOfCartons: undefined,
  unitRrp: undefined,
  grossProfitPercentage: null
}

const VolumePromotionHelper = {
  validatePromotion: (promotion = {}) => {
    const validationMessages = []
    const isServiceProduct = !!promotion.isServiceProduct
    const isUsTenant = !!promotion.isUsTenant
    if (!promotion.selectedPromotionType) {
      validationMessages.push({
        fieldName: 'selectedPromotionType',
        message: 'Promotion Type is required'
      })
    }
    if (
      promotion.selectedPromotionType === PROMOTION_TYPE.BY_FAMILY_GROUP &&
      !promotion.selectedFamilyGroup
    ) {
      validationMessages.push({
        fieldName: 'selectedFamilyGroup',
        message: 'Family Group selection required'
      })
    } else if (
      promotion.selectedPromotionType === PROMOTION_TYPE.BY_PRODUCT &&
      !promotion.selectedProduct
    ) {
      validationMessages.push({
        fieldName: 'selectedProduct',
        message: 'Product selection required'
      })
    }
    if (!promotion.promotionName) {
      validationMessages.push({
        fieldName: 'promotionName',
        message: 'Promotion name required'
      })
    } else if (promotion.promotionName.length > 60) {
      validationMessages.push({
        fieldName: 'promotionName',
        message: 'Promotion name cannot exceed 60 characters'
      })
    }
    if (!promotion.startDate) {
      validationMessages.push({
        fieldName: 'startDate',
        message: 'Start Date is required'
      })
    }
    if (!promotion.endDate) {
      validationMessages.push({
        fieldName: 'endDate',
        message: 'End Date is required'
      })
    }
    const hasValidDateRange =
      moment(promotion.startDate, moment.ISO_8601, true).isValid() &&
      moment(promotion.endDate, moment.ISO_8601, true).isValid()
    if (
      hasValidDateRange &&
      moment(promotion.startDate).isAfter(promotion.endDate)
    ) {
      validationMessages.push({
        fieldName: 'startDate',
        message: 'Start Date cannot be after the End Date'
      })
    }
    if (!promotion.targetType) {
      validationMessages.push({
        fieldName: 'targetType',
        message: 'Target type selection required'
      })
    }
    if (
      promotion.targetType === TARGET_TYPE.BANNER_GROUP &&
      (!promotion.targetBannerGroupIds ||
        promotion.targetBannerGroupIds.length === 0)
    ) {
      validationMessages.push({
        fieldName: 'targetBannerGroupIds',
        message: 'Banner Group(s) required'
      })
    } else if (
      promotion.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR &&
      (!promotion.targetMultiStoreOperatorIds ||
        promotion.targetMultiStoreOperatorIds.length === 0)
    ) {
      validationMessages.push({
        fieldName: 'targetMultiStoreOperatorIds',
        message: 'Multi-Store Operator(s) required'
      })
    } else if (
      promotion.targetType === TARGET_TYPE.RETAILER_STORE &&
      (!promotion.targetStoreIds || promotion.targetStoreIds.length === 0)
    ) {
      validationMessages.push({
        fieldName: 'targetStoreIds',
        message: 'Retailer(s) required'
      })
    }

    if (!promotion.zonePrices || promotion.zonePrices.length === 0) {
      validationMessages.push({
        fieldName: 'zonePrices',
        message: 'Zone Price(s) required'
      })
    }

    if (promotion.zonePrices) {
      const validatableRegionPriceMapRows = promotion.zonePrices.filter(
        (row) => row.tiers.length > 0
      )

      if (validatableRegionPriceMapRows.length === 0) {
        validationMessages.push({
          fieldName: 'zonePrices',
          message: "Zone Price's tier(s) required"
        })
      }

      validatableRegionPriceMapRows.forEach((zonePrice) => {
        const cartonPrice = zonePrice.cartonPrice

        // ✅ Ensure we validate tiers in ascending order of numberOfCartons
        const sortedTiers = [...zonePrice.tiers].sort(
          (a, b) => a.numberOfCartons - b.numberOfCartons
        )

        let previousTier = null

        sortedTiers.forEach((tier) => {
          if (!tier.numberOfCartons || tier.numberOfCartons <= 0) {
            validationMessages.push({
              fieldName: 'numberOfCartons',
              message: 'No. of cartons must be positive'
            })
          }

          if (!tier.amountOff || tier.amountOff <= 0) {
            validationMessages.push({
              fieldName: 'amountOff',
              message: 'Amount Off must be positive'
            })
          }

          if (tier.amountOff > cartonPrice) {
            validationMessages.push({
              fieldName: 'amountOff',
              message: 'Amount Off must be lower than Carton Price'
            })
          }

          if (previousTier && tier.amountOff < previousTier.amountOff) {
            validationMessages.push({
              fieldName: 'amountOff',
              message: `Amount Off for ${tier.numberOfCartons} cartons must be equal to or greater than the previous tier (${previousTier.numberOfCartons} cartons).`
            })
          }

          if (!tier.unitRrp || tier.unitRrp <= 0) {
            if (!isServiceProduct && !isUsTenant) {
              validationMessages.push({
                fieldName: 'unitRrp',
                message: 'RRP / KG must be positive'
              })
            }
          }

          if (
            !isServiceProduct &&
            !isUsTenant &&
            (!tier.grossProfitPercentage || tier.grossProfitPercentage <= 0)
          ) {
            validationMessages.push({
              fieldName: 'grossProfitPercentage',
              message: 'GP% must be positive'
            })
          }

          previousTier = tier
        })
      })
    }

    if (
      promotion.zonePrices &&
      VolumePromotionHelper.hasUnsavedTiers(promotion.zonePrices) &&
      VolumePromotionHelper.hasValidTier(promotion.zonePrices)
    ) {
      validationMessages.push({
        fieldName: 'notApplicable',
        message: 'This Promotion has Volume Tiers that have not been added yet'
      })
    }

    return validationMessages
  },

  checkForDuplicatePromotion(promotions, promotion, promotionIndex) {
    return promotions.reduce((acc, currPromotion, currPromotionIndex) => {
      if (currPromotionIndex !== promotionIndex) {
        if (JSON.stringify(currPromotion) === JSON.stringify(promotion)) {
          return acc.concat(currPromotionIndex)
        }
      }
      return acc
    }, [])
  },

  checkForDuplicatePromotions(promotions) {
    let duplicatePromotionIndexes = []
    promotions.forEach((promotion, promotionIndex) => {
      duplicatePromotionIndexes = duplicatePromotionIndexes.concat(
        VolumePromotionHelper.checkForDuplicatePromotion(
          promotions,
          promotion,
          promotionIndex
        )
      )
    })
    return duplicatePromotionIndexes
  },

  validatePromotions: (promotions) => {
    for (const promotion of promotions) {
      const validation = VolumePromotionHelper.validatePromotion(promotion)
      if (validation.length > 0) {
        return false
      }
    }
    const duplicates =
      VolumePromotionHelper.checkForDuplicatePromotions(promotions)
    return duplicates.length <= 0
  },

  addPricingRegionsInfo: (
    zonePrices,
    pricingRegions,
    tenantCountryRegion = 'Australia'
  ) => {
    return zonePrices.map((zone) => {
      const zoneInfo = pricingRegions.find(
        (region) => region.code === zone.code
      )

      return {
        ...zone,
        name: zoneInfo.name,
        displayName: formatRegionDisplayName(zoneInfo, tenantCountryRegion)
      }
    })
  },

  sortZoneCodesWithPrice: (zones) => {
    return Object.values(zones).sort((d1, d2) =>
      d1.displayName > d2.displayName ? 1 : -1
    )
  },

  mappedZoneCodesWithPrice: (zones) => {
    return zones.map((zone) => {
      return {
        ...zone,
        tiers: [],
        newTier: { ...DEFAULT_PROMOTION_TIER }
      }
    })
  },

  transformFamilyGroupZonePricesToRegionalPriceMap: (
    zonePrices,
    pricingRegions,
    tenantCountryRegion = 'Australia'
  ) => {
    let pricedZones = []
    pricedZones = zonePrices.map((zone) => {
      return {
        code: zone.pricingRegionCode,
        cartonPrice: zone.priceExGst,
        originalUnitRrp: zone.regionUnitRrp || zone.rrp || null
      }
    })

    pricedZones = VolumePromotionHelper.addPricingRegionsInfo(
      pricedZones,
      pricingRegions,
      tenantCountryRegion
    )
    pricedZones = VolumePromotionHelper.sortZoneCodesWithPrice(pricedZones)
    return VolumePromotionHelper.mappedZoneCodesWithPrice(pricedZones)
  },

  addProductCartonPricePerZone: (zones, regionalPriceMap) => {
    return zones.map((zone) => {
      const regionPriceData = Object.entries(regionalPriceMap).find(
        (region) => region[0] === zone.code
      )
      return {
        ...zone,
        cartonPrice: regionPriceData?.[1]?.[0]?.price,
        originalUnitRrp:
          regionPriceData?.[1]?.[0]?.regionUnitRrp ||
          regionPriceData?.[1]?.[0]?.rrp ||
          null
      }
    })
  },

  transformProductZonePricesToRegionalPriceMap: (
    zonePrices,
    regionalPriceMap,
    tenantCountryRegion = 'Australia'
  ) => {
    let pricedZones = zonePrices

    pricedZones = VolumePromotionHelper.addProductCartonPricePerZone(
      pricedZones,
      regionalPriceMap
    )
    pricedZones = pricedZones.map((zone) => ({
      ...zone,
      displayName:
        zone.displayName || formatRegionDisplayName(zone, tenantCountryRegion)
    }))
    pricedZones = VolumePromotionHelper.sortZoneCodesWithPrice(pricedZones)

    return VolumePromotionHelper.mappedZoneCodesWithPrice(pricedZones)
  },

  mappedForAPI: (zonePrices, isServiceProduct = false) => {
    // remove zones without tiers
    const filteredZones = zonePrices.filter((zones) => zones.tiers.length)

    return filteredZones.map((zone) => {
      const tierFallbackPrice = Number(zone.cartonPrice)
      return {
        pricingRegionCode: zone.code,
        tiers: zone.tiers.map(
          ({ grossProfitPercentage, discountPercentage, ...tier }) => {
            const amountOff = Number(tier.amountOff)
            const fallbackUnitCostExTax =
              Number.isFinite(tierFallbackPrice) && Number.isFinite(amountOff)
                ? tierFallbackPrice - amountOff
                : null
            const normalizedUnitPrice =
              fallbackUnitCostExTax > 0 ? fallbackUnitCostExTax : null

            return {
              ...tier,
              unitRrp:
                tier.unitRrp ||
                tier.revisedTierUnitPriceIncTax ||
                tier.revisedTierUnitPriceExTax ||
                normalizedUnitPrice,
              revisedTierUnitPriceExTax:
                tier.revisedTierUnitPriceExTax || normalizedUnitPrice,
              revisedTierUnitPriceIncTax:
                tier.revisedTierUnitPriceIncTax ||
                (isServiceProduct ? normalizedUnitPrice : null)
            }
          }
        )
      }
    })
  },

  isPromotionValid: (promotion) => {
    const validation = VolumePromotionHelper.validatePromotion(promotion)
    return validation.length === 0
  },

  isRowDuplicate: (promotions, promotion, promotionIndex) => {
    return (
      VolumePromotionHelper.checkForDuplicatePromotion(
        promotions,
        promotion,
        promotionIndex
      ).length > 0
    )
  },

  isZoneNewTierValid: (zone, isUsTenant = false) => {
    const newTierCartons = Number(zone.newTier.numberOfCartons)
    const newAmountOff = Number(zone.newTier.amountOff)

    const sortedTiers = [...zone.tiers].sort(
      (a, b) => a.numberOfCartons - b.numberOfCartons
    )

    const lowerTier = sortedTiers
      .filter((tier) => tier.numberOfCartons < newTierCartons)
      .pop()

    const higherTier = sortedTiers.find(
      (tier) => tier.numberOfCartons > newTierCartons
    )

    const respectsLowerTier =
      !lowerTier || newAmountOff >= Number(lowerTier.amountOff)

    const respectsHigherTier =
      !higherTier || newAmountOff <= Number(higherTier.amountOff)

    return (
      [
        zone.newTier.numberOfCartons,
        zone.newTier.amountOff,
        ...(isUsTenant
          ? []
          : [zone.newTier.unitRrp, zone.newTier.grossProfitPercentage])
      ].every((p) => p > 0) &&
      zone.newTier.amountOff <= zone.cartonPrice &&
      !zone.tiers.find((tier) => tier.numberOfCartons === newTierCartons) &&
      respectsLowerTier &&
      respectsHigherTier
    )
  },

  unsavedTiersQuantity: (zonePrices) => {
    return zonePrices.filter((zone) => {
      let hasUncompletedTier = false
      const candidateTier = Array.isArray(zone.newTier)
        ? zone.newTier[0]
        : zone.newTier

      if (!candidateTier) return false

      for (const prop in candidateTier) {
        if (
          candidateTier[prop] !== undefined &&
          candidateTier[prop] !== null &&
          candidateTier[prop] !== '' &&
          candidateTier[prop] > 0
        )
          hasUncompletedTier = true
      }

      if (hasUncompletedTier) return candidateTier
    })?.length
  },

  hasUnsavedTiers: (zonePrices) => {
    return !!VolumePromotionHelper.unsavedTiersQuantity(zonePrices)
  },

  hasValidTier: (zonePrices) => {
    return zonePrices?.find((zone) => zone.tiers?.length > 0)
  }
}

export default VolumePromotionHelper
